'use client';

import NextLink, { type LinkProps } from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import { forwardRef, Suspense, useCallback, useEffect, useState } from "react";

function stripFinalSlash(url: string) {
	return url.endsWith('/') ? url.slice(0, -1) : url;
}

function getWindowURL() {
	if (typeof window === 'undefined') return new URL('http://localhost/');
	return new URL(window.location.href);
}

function LinkComponentParent(props: Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, keyof LinkProps> & Omit<LinkProps, 'href'> & {
	readonly children?: React.ReactNode | undefined;
	readonly href: string;
} & React.RefAttributes<HTMLAnchorElement>, ref: React.Ref<HTMLAnchorElement>) {
	return <Suspense fallback={<LinkComponentBase {...props} currentSearchParams={null} ref={ref} />}>
		<LinkComponentWithSearchParams {...props} ref={ref} />
	</Suspense>;
}

export const Link = forwardRef(LinkComponentParent);

const LinkComponentWithSearchParams = forwardRef(function LinkComponentWithSearchParams(props: Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, keyof LinkProps> & Omit<LinkProps, 'href'> & {
	readonly children?: React.ReactNode | undefined;
	readonly href: string;
} & React.RefAttributes<HTMLAnchorElement>, ref: React.Ref<HTMLAnchorElement>) {
	const [windowURL, setWindowURL] = useState(getWindowURL());
	const recalcWindowURL = useCallback(() => setWindowURL((existing) => {
		const newer = getWindowURL();
		if (existing.href !== newer.href) return newer;
		else return existing;
	}), []);

	const currentSearchParams = useSearchParams(); // also registers for hash change events, though this is undocumented

	// Next.js only updates the hash *after* currentSearchParams causes its rerenders,
	// so we need to delay our re-render a bit to ensure the hash is correct when we read it in LinkComponentBase.
	// We also don't know when the URL will actually update, so we'll use a series of timeouts to ensure we catch it at some point.
	//
	// Oh, the things we do to hack around framework limitations.
	useEffect(() => {
		const timeouts = [
			setTimeout(recalcWindowURL, 50),
			setTimeout(recalcWindowURL, 250),
			setTimeout(recalcWindowURL, 500),
			setTimeout(recalcWindowURL, 1000),
			setTimeout(recalcWindowURL, 2000),
			setTimeout(recalcWindowURL, 3000),
		];
		return () => timeouts.forEach(t=>clearTimeout(t));
	});

	return <LinkComponentBase {...props} currentSearchParams={currentSearchParams} ref={ref} windowUrl={windowURL} />;
});

const LinkComponentBase = forwardRef(function LinkComponentBase({ href, children, currentSearchParams, windowUrl = getWindowURL(), ...props }: Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, keyof LinkProps> & Omit<LinkProps, 'href'> & {
	readonly children?: React.ReactNode | undefined;
	readonly href: string;
	readonly currentSearchParams: URLSearchParams | null;
	readonly windowUrl?: URL;
} & React.RefAttributes<HTMLAnchorElement>, ref: React.Ref<HTMLAnchorElement>) {

	const currentPathname = usePathname();

	const hrefAsUrl = new URL(href, windowUrl);
	const isCurrentOrigin = hrefAsUrl.origin === windowUrl.origin;
	const isCurrent = isCurrentOrigin && stripFinalSlash(hrefAsUrl.pathname) === stripFinalSlash(currentPathname) && hrefAsUrl.hash === windowUrl.hash;

	//console.log('LinkComponent', { href, windowUrl, hrefAsUrl, currentPathname, currentSearchParams, isCurrent });

	// mainly here to persist the realName search param across navigation
	if (currentSearchParams && (hrefAsUrl.search === '' || !isCurrent || hrefAsUrl.pathname !== '')) hrefAsUrl.search = currentSearchParams.toString();

	return <NextLink target={isCurrentOrigin ? undefined : '_blank'} {...props} href={isCurrentOrigin ? `${hrefAsUrl.pathname}${hrefAsUrl.search}${hrefAsUrl.hash}` : hrefAsUrl} aria-current={isCurrent ? 'page' : undefined} ref={ref}>
		{children}
	</NextLink>;
});
